'use client';

import {
  Alert,
  Button,
  CardTable,
  Checkbox,
  Flex,
  Icon,
  Text,
  CellContainer,
  CellCheckbox,
} from '@/_components';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { attachDocumentsToQuestionnaire } from '@/_api/client-questionnaires';
import { useCallback, useContext, useEffect, useState, type FC } from 'react';
import type { UnattachedPreviewTabProps } from './UnattachedPreviewTab.types';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { downloadDocument } from '@/_api/client-documents';

export const UnattachedPreviewTab: FC<UnattachedPreviewTabProps> = ({
  hideDefaultAlert,
  from3MonthsAgo,
  initialDocuments,
  onDocumentsChanged,
  questionnaire,
  reloadQuestionnairePreview,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { setPreviewFile, getDocumentsUploadedOutside } =
    useContext(PreviewContext);

  const [selectedDocuments, setSelectedDocuments] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>(initialDocuments ?? []);

  useEffect(() => {
    const loadDocuments = async () => {
      // Only load documents if not provided initially
      if (
        !initialDocuments &&
        getDocumentsUploadedOutside &&
        questionnaire?.company_id
      ) {
        try {
          const response = await getDocumentsUploadedOutside(
            questionnaire.company_id,
            !!from3MonthsAgo,
          );
          if (!response.error) {
            setDocuments(response ?? []);
          }
        } catch (error) {
          setDocuments([]);
        }
      }
    };

    loadDocuments();
  }, [
    getDocumentsUploadedOutside,
    questionnaire?.company_id,
    from3MonthsAgo,
    initialDocuments,
  ]);

  const onAttachDocuments = useCallback(
    async (documents: any) => {
      try {
        setIsLoading(true);

        await attachDocumentsToQuestionnaire({
          companyId: questionnaire?.company_id!,
          questionnaireId: questionnaire?.id!,
          extractedDocumentIds: documents.map((document: any) => document.id),
          token,
        });

        await reloadQuestionnairePreview();

        if (initialDocuments && onDocumentsChanged) {
          await onDocumentsChanged();
        } else {
          const reloadedDocuments = await getDocumentsUploadedOutside(
            questionnaire?.company_id!,
            !!from3MonthsAgo,
          );
          setDocuments(reloadedDocuments ?? []);
        }

        addToast('success', 'Document(s) attached successfully');
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setIsLoading(false);
        setSelectedDocuments([]);
      }
    },
    [
      reloadQuestionnairePreview,
      questionnaire?.company_id,
      questionnaire?.id,
      token,
      addToast,
      getDocumentsUploadedOutside,
      initialDocuments,
      onDocumentsChanged,
      from3MonthsAgo,
    ],
  );
  const handleSelectDocument = (document: any) => {
    setSelectedDocuments((prev: any[]) =>
      prev.includes(document)
        ? prev.filter((d: any) => d.id !== document.id)
        : [...prev, document],
    );
  };

  return (
    <div>
      {!hideDefaultAlert && (
        <Alert type="info">
          This is a list of all documents uploaded outside of questionnaires.
          You can click a document to preview or download if preview is not
          supported. If you want to attach it to the questionnaire, use the
          &quot;Attach&quot; button.
        </Alert>
      )}
      <Flex isRow paddingX={4} isBetween={true} isYCentered={true}>
        <Text fontWeight="bold">
          Documents uploaded outside of the questionnaire
        </Text>
        <Button
          intent="secondary"
          value={`Attach ${selectedDocuments.length} selected`}
          onClick={() => onAttachDocuments(selectedDocuments)}
          isDisabled={selectedDocuments.length === 0}
        />
      </Flex>
      {documents.length === 0 && (
        <Text type="small">
          No documents uploaded outside of the questionnaire.
        </Text>
      )}
      {documents.length > 0 && (
        <CardTable isInline={true} isLoading={isLoading}>
          <table className="sorabanTable">
            <thead>
              <tr style={{ height: '50px' }}>
                <th>
                  <CellContainer isHeader={true}>
                    <Checkbox
                      isChecked={selectedDocuments.length === documents.length}
                      label=""
                      onChange={() => {
                        setSelectedDocuments(
                          selectedDocuments.length === documents.length
                            ? []
                            : documents,
                        );
                      }}
                    />
                  </CellContainer>
                </th>
                <th>
                  <CellContainer isHeader={true} text="Document Name" />
                </th>
                <th>
                  <CellContainer
                    isHeader={true}
                    text="Uploaded At"
                    width={100}
                  />
                </th>
                <th>
                  <CellContainer isHeader={true} text="Actions" width={75} />
                </th>
              </tr>
            </thead>
            <tbody>
              {documents.map((document: any) => {
                const documentName = document.source.split('/').pop();
                // show only date
                const formattedTime = new Date(
                  document.created_at,
                ).toLocaleDateString();
                const isSelected = selectedDocuments.includes(document);

                const onPreview = async () => {
                  const { document: doc } = await downloadDocument({
                    companyId: questionnaire?.company_id!,
                    link: document.source,
                    token,
                  });

                  setPreviewFile(doc?.data?.link);
                };

                return (
                  <tr key={document.id} style={{ height: '50px' }}>
                    <td>
                      <CellCheckbox isHeader={true}>
                        <Checkbox
                          isChecked={isSelected}
                          onChange={() => handleSelectDocument(document)}
                          label="checked"
                          isLabelHidden={true}
                        />
                      </CellCheckbox>
                    </td>
                    <td>
                      <CellContainer>
                        <Flex isRow isYCentered gap={4}>
                          <Icon name="file" size={16} />
                          <Text
                            color="accent"
                            fontWeight="semi-bold"
                            onClick={onPreview}
                          >
                            {documentName}
                          </Text>
                        </Flex>
                      </CellContainer>
                    </td>
                    <td>
                      <CellContainer width={100}>
                        <Text type="small" textAlign="right">
                          {formattedTime}
                        </Text>
                      </CellContainer>
                    </td>
                    <td>
                      <CellContainer width={75}>
                        <Button
                          size="small"
                          intent="primary"
                          onClick={() => onAttachDocuments([document])}
                          value="Attach"
                        />
                      </CellContainer>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardTable>
      )}
    </div>
  );
};
